import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { toast } from "sonner";
import { CheckCircle2, XCircle, ClipboardList, Factory, Quote, RefreshCw, ChevronDown, ChevronRight, FlaskConical, ShoppingCart, BarChart3 } from "lucide-react";

const SAMPLE_STATUSES = ["requested", "approved", "dispatched", "received", "revision_requested", "rejected"];

export default function AdminDashboard() {
  const [agents, setAgents] = useState([]);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [requirements, setRequirements] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [resources, setResources] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [knowledge, setKnowledge] = useState([]);

  const [tab, setTab] = useState("requirements");
  const [expanded, setExpanded] = useState(null);
  const [details, setDetails] = useState({});
  const [participants, setParticipants] = useState({});
  const [samples, setSamples] = useState([]);
  const [sampleFilter, setSampleFilter] = useState("all");
  const [sampleBusy, setSampleBusy] = useState("");
  const [orders, setOrders] = useState([]);
  const [auditEvents, setAuditEvents] = useState({});
  const [reporting, setReporting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const load = async () => {
    setLoading(true);
    setLoadError("");
    const endpoints = [
      { key: 'agents', url: `${API}/admin/agents`, setter: setAgents },
      { key: 'stats', url: `${API}/admin/stats`, setter: setStats },
      { key: 'requirements', url: `${API}/admin/requirements`, setter: setRequirements },
      { key: 'samples', url: `${API}/admin/samples`, setter: setSamples },
      { key: 'purchase-orders', url: `${API}/admin/purchase-orders`, setter: setOrders },
      { key: 'reporting', url: `${API}/admin/reporting`, setter: setReporting },
      { key: 'templates', url: `${API}/admin/templates`, setter: (d) => setTemplates(d || []) },
      { key: 'resources', url: `${API}/admin/resources`, setter: (d) => setResources(d || []) },
      { key: 'sectors', url: `${API}/admin/sectors`, setter: (d) => setSectors(d || []) },
      { key: 'users', url: `${API}/admin/users`, setter: (d) => setUsers(d || []) },
      { key: 'knowledge', url: `${API}/admin/knowledge`, setter: (d) => setKnowledge(d || []) },
    ];

    const results = await Promise.allSettled(endpoints.map(e => axios.get(e.url)));
    const failures = [];

    results.forEach((res, idx) => {
      if (res.status === 'fulfilled') {
        endpoints[idx].setter(res.value.data);
      } else {
        failures.push(res.reason);
        console.error(`Failed to load ${endpoints[idx].key}:`, res.reason);
      }
    });
    if (failures.length) {
      const sessionExpired = failures.every((failure) => failure?.response?.status === 401);
      setLoadError(sessionExpired
        ? "Your admin session has expired. Sign in again, then reload this page."
        : "Some dashboard data could not be loaded. Try again to refresh the control room.");
    }
    setLoading(false);
  };

  const createSample = async (rfqId, quoteId) => {
    setSampleBusy(quoteId);
    try {
      await axios.post(`${API}/admin/samples`, {
        rfq_id: rfqId,
        quote_id: quoteId,
        description: "Admin-requested production sample",
        quantity: 1,
      });
      toast.success("Sample request created");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Sample request failed"); }
    finally { setSampleBusy(""); }
  };

  const createOrder = async (rfqId, quoteId) => {
    try {
      await axios.post(`${API}/admin/purchase-orders`, { rfq_id: rfqId, quote_id: quoteId, notes: "Created from Admin Control Room" });
      toast.success("Purchase order created");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Purchase order failed"); }
  };

  const updateRequirement = async (rfqId, status) => {
    try {
      await axios.patch(`${API}/admin/requirements/${rfqId}/status`, { status });
      toast.success("Requirement status updated");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Update failed"); }
  };

  const updateVisibility = async (agentId, isPublic) => {
    try {
      await axios.post(`${API}/admin/agents/${agentId}/visibility`, { is_public: isPublic });
      toast.success(isPublic ? "Profile made public" : "Profile kept private");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Visibility update failed"); }
  };

  const showDetails = async (rfqId) => {
    if (expanded === rfqId) return setExpanded(null);
    try {
      const { data } = await axios.get(`${API}/rfqs/${rfqId}`);
      setDetails((prev) => ({ ...prev, [rfqId]: data }));
      const participantResponse = await axios.get(`${API}/admin/rfqs/${rfqId}/participants`);
      const auditResponse = await axios.get(`${API}/admin/rfqs/${rfqId}/audit-events`);
      setParticipants((prev) => ({ ...prev, [rfqId]: participantResponse.data }));
      setAuditEvents((prev) => ({ ...prev, [rfqId]: auditResponse.data }));
      setExpanded(rfqId);
    } catch { toast.error("Unable to load requirement"); }
  };

  const updateParticipant = async (rfqId, agentId, action) => {
    try {
      if (action === "remove") {
        await axios.delete(`${API}/admin/rfqs/${rfqId}/manufacturers/${agentId}`);
      } else {
        await axios.post(`${API}/admin/rfqs/${rfqId}/manufacturers/${agentId}/${action}`);
      }
      const { data } = await axios.get(`${API}/admin/rfqs/${rfqId}/participants`);
      setParticipants((prev) => ({ ...prev, [rfqId]: data }));
      toast.success(action === "remove" ? "Manufacturer removed" : "Invitation sent");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Participant update failed");
    }
  };

  const updateBidding = async (rfqId, action) => {
    try {
      if (action === "reopen-rfq") {
        await axios.patch(`${API}/admin/requirements/${rfqId}/status`, { status: "open" });
        const { data } = await axios.get(`${API}/rfqs/${rfqId}`);
        setDetails((prev) => ({ ...prev, [rfqId]: data }));
        load();
        toast.success("RFQ reopened");
        return;
      }
      let payload;
      if (action === "extend") {
        const hours = Number(window.prompt("Extend bidding by how many hours?", "24"));
        if (!Number.isFinite(hours) || hours <= 0) return;
        payload = { extension_hours: hours };
      }
      await axios.post(`${API}/admin/rfqs/${rfqId}/bidding/${action}`, payload);
      const { data } = await axios.get(`${API}/rfqs/${rfqId}`);
      setDetails((prev) => ({ ...prev, [rfqId]: data }));
      load();
      toast.success(`Bidding ${action === "extend" ? "deadline extended" : `${action}ed`}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bidding update failed");
    }
  };

  const updateQuote = async (quoteId, status, rfqId) => {
    try {
      await axios.patch(`${API}/admin/quotes/${quoteId}/status`, { status });
      toast.success("Quote status updated");
      const { data } = await axios.get(`${API}/rfqs/${rfqId}`);
      setDetails((prev) => ({ ...prev, [rfqId]: data }));
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Update failed"); }
  };
  useEffect(() => { load(); }, []);

  const verify = async (id, v) => {
    try {
      await axios.post(`${API}/admin/agents/${id}/${v ? "verify" : "unverify"}`);
      toast.success(v ? "Verified" : "Unverified");
      load();
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="min-h-screen bg-bone">
      <Navbar />
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
        <div className="overline mb-3 text-burn">§ ADMIN</div>
        <h1 className="font-serif text-5xl lg:text-6xl font-light leading-none tracking-tight mb-10">Control Room.</h1>

        <div className="flex gap-2 mb-8 flex-wrap">
          <AdminTab active={tab === "requirements"} onClick={() => setTab("requirements")} icon={ClipboardList} label="Requirements" count={requirements.length} />
          <AdminTab active={tab === "templates"} onClick={() => setTab("templates")} icon={ClipboardList} label="Templates" count={templates.length} />
          <AdminTab active={tab === "resources"} onClick={() => setTab("resources")} icon={ClipboardList} label="Resources" count={resources.length} />
          <AdminTab active={tab === "manufacturers"} onClick={() => setTab("manufacturers")} icon={Factory} label="Manufacturers" count={agents.length} />
          <AdminTab active={tab === "users"} onClick={() => setTab("users")} icon={Factory} label="Users" count={users.length} />
          <AdminTab active={tab === "knowledge"} onClick={() => setTab("knowledge")} icon={ClipboardList} label="Categories & Knowledge" count={sectors.length + knowledge.length} />

          <AdminTab active={tab === "quotes"} onClick={() => setTab("quotes")} icon={Quote} label="Quotes" count={stats?.quotes || 0} />
          <AdminTab active={tab === "samples"} onClick={() => setTab("samples")} icon={FlaskConical} label="Samples" count={reporting?.samples?.total ?? samples.length} />
          <AdminTab active={tab === "procurement"} onClick={() => setTab("procurement")} icon={ShoppingCart} label="Procurement" count={orders.length} />
          <AdminTab active={tab === "reporting"} onClick={() => setTab("reporting")} icon={BarChart3} label="Analytics" count={reporting?.rfqs?.total || 0} />
        </div>

        {loadError && (
          <div className="mb-8 border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-900 flex items-center justify-between gap-4">
            <span>{loadError}</span>
            <button type="button" className="btn-outline text-xs shrink-0" onClick={load}>Retry</button>
          </div>
        )}

        {loading && <div className="mb-8 font-mono text-xs text-[--muted-foreground]">Loading control room data...</div>}

        {stats && (
          <div className="grid md:grid-cols-5 gap-4 mb-12">
            {[["Users", stats.users], ["Agents", stats.agents], ["Verified", stats.verified_agents], ["RFQs", stats.rfqs], ["Quotes", stats.quotes]].map(([k, v]) => (
              <div key={k} className="editorial-card p-6">
                <div className="font-serif text-4xl tracking-tight">{v}</div>
                <div className="overline mt-2 text-[10px]">{k}</div>
              </div>
            ))}
          </div>
        )}

        {stats && <Pipeline stats={stats} />}

        {reporting && (
          <section className="mb-12" aria-labelledby="sample-statistics-heading">
            <h2 id="sample-statistics-heading" className="overline mb-4">§ SAMPLE REQUESTS · CLICK TO REVIEW</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-2">
              <button type="button" onClick={() => { setSampleFilter("all"); setTab("samples"); }} className="editorial-card p-4 text-left hover:border-ink" data-testid="sample-count-all">
                <div className="font-serif text-3xl">{reporting.samples?.total || 0}</div><div className="font-mono text-[10px] uppercase mt-2">All requests</div>
              </button>
              {SAMPLE_STATUSES.map((status) => (
                <button key={status} type="button" onClick={() => { setSampleFilter(status); setTab("samples"); }} className="editorial-card p-4 text-left hover:border-ink" data-testid={`sample-count-${status}`}>
                  <div className="font-serif text-3xl">{reporting.samples?.by_status?.[status] || 0}</div><div className="font-mono text-[10px] uppercase mt-2">{status.replaceAll("_", " ")}</div>
                </button>
              ))}
            </div>
          </section>
        )}

        {tab === "requirements" && (
          <>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-3xl tracking-tight">Requirement pipeline</h2>
              <button onClick={load} className="btn-outline text-xs"><RefreshCw className="w-3 h-3" /> Refresh</button>
            </div>
            <div className="space-y-3 mb-12">
              {requirements.map((r) => (
                <div key={r.rfq_id} className="editorial-card overflow-hidden">
                  <button onClick={() => showDetails(r.rfq_id)} className="w-full text-left p-5 flex items-center justify-between gap-4">
                    <div>
                      <div className="overline mb-2">{r.rfq_id} · {r.category}</div>
                      <div className="font-serif text-2xl">{r.title}</div>
                      <div className="font-mono text-xs text-[--muted-foreground] mt-1">{r.buyer_name} · {r.quote_count} quotes · {r.shortlisted_count} shortlisted</div>
                    </div>
                    <div className="flex items-center gap-3">
                      <select value={r.status} onClick={(e) => e.stopPropagation()} onChange={(e) => updateRequirement(r.rfq_id, e.target.value)} className="input-underline text-xs">
                        {["open", "under_review", "ready_for_bidding", "bidding", "quotes_received", "shortlisting", "sampling", "selected", "procurement", "closed"].map((s) => <option key={s} value={s}>{s.replaceAll("_", " ")}</option>)}
                      </select>
                      <ChevronDown className={`w-4 h-4 transition-transform ${expanded === r.rfq_id ? "rotate-180" : ""}`} />
                    </div>
                  </button>
                  {expanded === r.rfq_id && details[r.rfq_id] && (
                    <>
                      <ParticipantPanel
                        agents={agents}
                        participants={participants[r.rfq_id] || []}
                        onUpdate={(agentId, action) => updateParticipant(r.rfq_id, agentId, action)}
                      />
                      <BiddingPanel rfq={details[r.rfq_id].rfq} onUpdate={(action) => updateBidding(r.rfq_id, action)} />
                      <AuditPanel events={auditEvents[r.rfq_id] || []} />
                      <QuotePanel data={details[r.rfq_id]} onUpdate={updateQuote} samples={samples} onCreateSample={createSample} sampleBusy={sampleBusy} orders={orders} onCreateOrder={createOrder} />
                    </>
                  )}
                </div>
              ))}
              {requirements.length === 0 && <div className="editorial-card p-10 text-center font-mono text-sm">— No requirements yet.</div>}
            </div>
          </>
        )}

        {tab === "templates" && <TemplateBuilder templates={templates} sectors={sectors} onRefresh={load} />}
        {tab === "resources" && <ResourceBuilder resources={resources} sectors={sectors} onRefresh={load} />}
        {tab === "knowledge" && <KnowledgeAndSectors sectors={sectors} knowledge={knowledge} onReload={load} />}
        {tab === "manufacturers" && <ManufacturerPanel agents={agents} verify={verify} updateVisibility={updateVisibility} />}
        {tab === "users" && <UserManagement users={users} onRefresh={load} />}
        {tab === "quotes" && <QuoteOverview requirements={requirements} onOpen={(id) => { setTab("requirements"); showDetails(id); }} />}
        {tab === "samples" && <SampleOverview samples={samples} filter={sampleFilter} onFilter={setSampleFilter} />}
        {tab === "procurement" && <ProcurementOverview orders={orders} />}
        {tab === "reporting" && <ReportingOverview reporting={reporting} />}
      </div>
    </div>
  );
}

function UserManagement({ users, onRefresh }) {
  const [busy, setBusy] = useState("");

  const update = async (userId, payload) => {
    setBusy(userId);
    try {
      await axios.patch(`${API}/admin/users/${userId}`, payload);
      toast.success("User access updated");
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Unable to update user access");
    } finally { setBusy(""); }
  };

  const revokeSessions = async (userId) => {
    setBusy(userId);
    try {
      const { data } = await axios.post(`${API}/admin/users/${userId}/revoke-sessions`);
      toast.success(`${data.revoked} session${data.revoked === 1 ? "" : "s"} revoked`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Unable to revoke sessions");
    } finally { setBusy(""); }
  };

  return <div className="editorial-card overflow-x-auto">
    <div className="p-6 border-b border-[--border-soft]"><h2 className="font-serif text-3xl">User management</h2><p className="text-sm text-[--muted-foreground] mt-2">Approve manufacturer accounts, suspend access, change roles, and end active sessions.</p></div>
    <table className="w-full min-w-[880px] text-sm"><thead className="text-left border-b border-[--border-soft] text-[--muted-foreground]"><tr><th className="p-4">User</th><th className="p-4">Role</th><th className="p-4">Status</th><th className="p-4">Sign-in</th><th className="p-4">Controls</th></tr></thead>
      <tbody>{users.map((account) => <tr key={account.user_id} className="border-b border-[--border-soft]">
        <td className="p-4"><div className="font-medium">{account.name}</div><div className="text-xs text-[--muted-foreground]">{account.email}</div></td>
        <td className="p-4"><select className="input-underline text-xs" value={account.role} disabled={busy === account.user_id} onChange={(event) => update(account.user_id, { role: event.target.value })}>{["buyer", "agent", "admin"].map((role) => <option key={role} value={role}>{role}</option>)}</select></td>
        <td className="p-4"><select className="input-underline text-xs" value={account.account_status || "active"} disabled={busy === account.user_id} onChange={(event) => update(account.user_id, { account_status: event.target.value })}>{["active", "pending_approval", "suspended"].map((status) => <option key={status} value={status}>{status.replaceAll("_", " ")}</option>)}</select></td>
        <td className="p-4 text-xs">{(account.auth_providers || []).join(", ") || "legacy"}{account.email_verified === false && " · unverified"}</td>
        <td className="p-4"><button className="btn-outline text-[10px] py-2 px-3" disabled={busy === account.user_id} onClick={() => revokeSessions(account.user_id)}>End sessions</button></td>
      </tr>)}</tbody>
    </table>
    {users.length === 0 && <div className="p-10 font-mono text-sm text-center">— No user accounts.</div>}
  </div>;
}

const EMPTY_TEMPLATE_FORM = { name: "", sector: "", category: "", description: "", is_public: true, active: true };
const EMPTY_SKU_COLUMN = { key: "", label: "", type: "text", options: "", required: false };

function TemplateBuilder({ templates, sectors, onRefresh }) {
  const [form, setForm] = useState(EMPTY_TEMPLATE_FORM);
  const [skuColumns, setSkuColumns] = useState([{ ...EMPTY_SKU_COLUMN }]);
  // Rule #1: templates stay editable after publishing. `editingId` tracks
  // whether the form is creating a new template or updating an existing one.
  const [editingId, setEditingId] = useState(null);
  const [historyFor, setHistoryFor] = useState(null);
  const [history, setHistory] = useState(null);

  const resetForm = () => {
    setEditingId(null);
    setForm(EMPTY_TEMPLATE_FORM);
    setSkuColumns([{ ...EMPTY_SKU_COLUMN }]);
  };

  const startEdit = (template) => {
    setEditingId(template.template_id);
    setForm({
      name: template.name || "",
      sector: template.sector || "",
      category: template.category || template.sector || "",
      description: template.description || "",
      is_public: template.is_public !== false,
      active: template.active !== false,
    });
    setSkuColumns((template.sku_columns || template.fields || []).map((column) => ({
      key: column.key || "",
      label: column.label || "",
      type: column.type || "text",
      options: (column.options || []).join(", "),
      required: Boolean(column.required),
    })));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async (event) => {
    event.preventDefault();
    const payload = {
      ...form,
      sector: form.sector.trim() || form.category.trim(),
      category: form.category.trim() || form.sector.trim(),
      fields: [],
      sku_columns: skuColumns.map((column) => ({
        ...column,
        key: column.key.trim(),
        label: column.label.trim(),
        options: (column.options || "").split(",").map((option) => option.trim()).filter(Boolean),
      })).filter((column) => column.key && column.label),
    };

    try {
      if (editingId) {
        await axios.patch(`${API}/admin/templates/${editingId}`, payload);
        toast.success("Template updated — a new version was published");
      } else {
        await axios.post(`${API}/admin/templates`, payload);
        toast.success("Template published");
      }
      resetForm();
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Unable to save template");
    }
  };

  const removeTemplate = async (templateId) => {
    try {
      await axios.delete(`${API}/admin/templates/${templateId}`);
      toast.success("Template deleted");
      if (editingId === templateId) resetForm();
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Unable to delete template");
    }
  };

  const viewHistory = async (templateId) => {
    if (historyFor === templateId) {
      setHistoryFor(null);
      setHistory(null);
      return;
    }
    try {
      const res = await axios.get(`${API}/admin/templates/${templateId}/versions`);
      setHistoryFor(templateId);
      setHistory(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Unable to load version history");
    }
  };

  return (
    <div className="grid lg:grid-cols-[1.2fr_0.8fr] gap-6">
      <div className="editorial-card p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-serif text-3xl tracking-tight">{editingId ? "Edit template" : "Template builder"}</h2>
          {editingId && <button type="button" onClick={resetForm} className="btn-outline text-xs">Cancel edit · new template</button>}
        </div>
        <form onSubmit={submit} className="space-y-5">
          <div className="grid md:grid-cols-2 gap-4">
            <FieldInline label="Template name">
              <input value={form.name} onChange={(e) => setForm((current) => ({ ...current, name: e.target.value }))} className="input-underline" placeholder="e.g. Lip care launch brief" />
            </FieldInline>
            <FieldInline label="Sector">
              <select value={form.sector} onChange={(e) => setForm((current) => ({ ...current, sector: e.target.value }))} className="input-underline">
                <option value="">— select sector —</option>
                {sectors.map((sector) => <option key={sector.sector_id || sector.name} value={sector.name || sector}>{sector.name || sector}</option>)}
              </select>
            </FieldInline>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <FieldInline label="Category">
              <select value={form.category} onChange={(e) => setForm((current) => ({ ...current, category: e.target.value }))} className="input-underline">
                <option value="">— same as sector —</option>
                {sectors.map((sector) => <option key={sector.sector_id || sector.name} value={sector.name || sector}>{sector.name || sector}</option>)}
              </select>
            </FieldInline>
            <FieldInline label="Visibility">
              <select value={form.is_public ? "public" : "private"} onChange={(e) => setForm((current) => ({ ...current, is_public: e.target.value === "public" }))} className="input-underline">
                <option value="public">Public</option>
                <option value="private">Private</option>
              </select>
            </FieldInline>
          </div>
          <FieldInline label="Description">
            <textarea value={form.description} onChange={(e) => setForm((current) => ({ ...current, description: e.target.value }))} className="input-underline min-h-[100px]" placeholder="Describe what this template is for" />
          </FieldInline>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="overline">SKU TABLE COLUMNS</div>
                <p className="text-xs text-[--muted-foreground] mt-1">SKU, Product name and Quantity are fixed. Define the remaining SKU columns here.</p>
              </div>
              <button type="button" onClick={() => setSkuColumns((current) => [...current, { ...EMPTY_SKU_COLUMN }])} className="btn-outline text-xs">Add column</button>
            </div>
            {skuColumns.map((field, index) => (
              <div key={index} className="border border-[--border-soft] p-4 rounded-md space-y-3">
                <div className="flex items-center justify-between">
                  <div className="font-mono text-[10px] uppercase">Column {index + 1}</div>
                  <button type="button" onClick={() => setSkuColumns((current) => current.filter((_, idx) => idx !== index))} className="text-xs text-[--muted-foreground]">Remove</button>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  <input value={field.key} onChange={(e) => setSkuColumns((current) => current.map((item, idx) => idx === index ? { ...item, key: e.target.value } : item))} className="input-underline" placeholder="column_key" />
                  <input value={field.label} onChange={(e) => setSkuColumns((current) => current.map((item, idx) => idx === index ? { ...item, label: e.target.value } : item))} className="input-underline" placeholder="Column label" />
                </div>
                <div className="grid md:grid-cols-3 gap-3">
                  <select value={field.type} onChange={(e) => setSkuColumns((current) => current.map((item, idx) => idx === index ? { ...item, type: e.target.value } : item))} className="input-underline">
                    <option value="text">Text</option>
                    <option value="select">Dropdown</option>
                    <option value="number">Number</option>
                  </select>
                  <input value={field.options} onChange={(e) => setSkuColumns((current) => current.map((item, idx) => idx === index ? { ...item, options: e.target.value } : item))} className="input-underline" placeholder="Options, comma separated" />
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={field.required} onChange={(e) => setSkuColumns((current) => current.map((item, idx) => idx === index ? { ...item, required: e.target.checked } : item))} /> Required</label>
                </div>
              </div>
            ))}
          </div>

          <div className="flex gap-3">
            <button type="submit" className="btn-primary">{editingId ? "Save & publish update" : "Publish template"}</button>
            <label className="inline-flex items-center gap-2 text-xs">
              <input type="checkbox" checked={form.active} onChange={(e) => setForm((current) => ({ ...current, active: e.target.checked }))} />
              Active
            </label>
          </div>
        </form>
      </div>

      <div className="editorial-card p-6">
        <h3 className="font-serif text-2xl tracking-tight mb-5">Published templates</h3>
        <div className="space-y-3">
          {templates.map((template) => (
            <div key={template.template_id} className="border border-[--border-soft] p-4 rounded-md">
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="font-serif text-xl">{template.name}</div>
                <div className="flex gap-2">
                  <button type="button" onClick={() => startEdit(template)} className="btn-outline text-xs">Edit</button>
                  <button type="button" onClick={() => removeTemplate(template.template_id)} className="btn-outline text-xs">Delete</button>
                </div>
              </div>
              <div className="font-mono text-[10px] text-[--muted-foreground] mb-2">{template.sector} · {template.category || template.sector} · v{template.version || 1}</div>
              <div className="flex flex-wrap gap-1 mb-2">
                {(template.sku_columns || []).slice(0, 5).map((column, idx) => <span key={`${template.template_id}-${column.key || idx}`} className="tag text-[10px]">{column.label || column.key}</span>)}
              </div>
              <div className="text-xs text-[--muted-foreground] mb-2">{template.description || "No description yet."}</div>
              <button type="button" onClick={() => viewHistory(template.template_id)} className="text-xs underline text-[--muted-foreground]">
                {historyFor === template.template_id ? "Hide version history" : "View version history"}
              </button>
              {historyFor === template.template_id && history && (
                <div className="mt-3 border-t border-[--border-soft] pt-3 space-y-2">
                  {history.history.length === 0 && <div className="font-mono text-[10px] text-[--muted-foreground]">No prior versions — this is the original.</div>}
                  {history.history.map((entry) => (
                    <div key={entry.version} className="font-mono text-[10px] text-[--muted-foreground]">
                      v{entry.version} — {entry.snapshot?.category || entry.snapshot?.sector} · archived {new Date(entry.archived_at).toLocaleString()}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
          {templates.length === 0 && <div className="font-mono text-sm text-[--muted-foreground]">— No templates yet.</div>}
        </div>
      </div>
    </div>
  );
}

function ResourceBuilder({ resources, sectors, onRefresh }) {
  const [form, setForm] = useState({
    title: "",
    short_description: "",
    content: "",
    category: "",
    resource_type: "article",
    author: "",
    industry_sector: [],
    tags: "",
    visibility: "public",
    status: "published",
  });

  const toggleSector = (name) => {
    setForm((current) => ({
      ...current,
      industry_sector: current.industry_sector.includes(name)
        ? current.industry_sector.filter((s) => s !== name)
        : [...current.industry_sector, name],
    }));
  };

  const submit = async (event) => {
    event.preventDefault();
    try {
      await axios.post(`${API}/admin/resources`, {
        ...form,
        tags: form.tags.split(",").map((tag) => tag.trim()).filter(Boolean),
        resource_type: form.resource_type || "article",
      });
      setForm({ title: "", short_description: "", content: "", category: "", resource_type: "article", author: "", industry_sector: [], tags: "", visibility: "public", status: "published" });
      toast.success("Resource published");
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Unable to save resource");
    }
  };

  const removeResource = async (resourceId) => {
    try {
      await axios.delete(`${API}/admin/resources/${resourceId}`);
      toast.success("Resource deleted");
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Unable to delete resource");
    }
  };

  return (
    <div className="grid lg:grid-cols-[1.15fr_0.85fr] gap-6">
      <div className="editorial-card p-6">
        <h2 className="font-serif text-3xl tracking-tight mb-6">Resource publisher</h2>
        <form onSubmit={submit} className="space-y-5">
          <div className="grid md:grid-cols-2 gap-4">
            <FieldInline label="Title">
              <input value={form.title} onChange={(e) => setForm((current) => ({ ...current, title: e.target.value }))} className="input-underline" placeholder="e.g. Beauty procurement checklist" />
            </FieldInline>
            <FieldInline label="Author">
              <input value={form.author} onChange={(e) => setForm((current) => ({ ...current, author: e.target.value }))} className="input-underline" placeholder="AskWinn team" />
            </FieldInline>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <FieldInline label="Category">
              <input value={form.category} onChange={(e) => setForm((current) => ({ ...current, category: e.target.value }))} className="input-underline" placeholder="Guide" />
            </FieldInline>
            <FieldInline label="Resource type">
              <select value={form.resource_type} onChange={(e) => setForm((current) => ({ ...current, resource_type: e.target.value }))} className="input-underline">
                <option value="article">Article</option>
                <option value="guide">Guide</option>
                <option value="video">Video</option>
                <option value="tutorial">Tutorial</option>
                <option value="educational_reference">Educational reference</option>
                <option value="brand_launch_resource">Brand launch resource</option>
                <option value="other">Other</option>
              </select>
            </FieldInline>
            <FieldInline label="Sector(s)">
              <div className="flex flex-wrap gap-2">
                {sectors.map((sector) => {
                  const name = sector.name || sector;
                  return (
                    <button type="button" key={sector.sector_id || name} onClick={() => toggleSector(name)} className={`tag ${form.industry_sector.includes(name) ? "verified" : ""} cursor-pointer`}>{name}</button>
                  );
                })}
              </div>
            </FieldInline>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <FieldInline label="Visibility">
              <select value={form.visibility} onChange={(e) => setForm((current) => ({ ...current, visibility: e.target.value }))} className="input-underline">
                <option value="public">Public</option>
                <option value="logged_in">Logged in</option>
                <option value="private">Private</option>
                <option value="admin_only">Admin only</option>
              </select>
            </FieldInline>
            <FieldInline label="Status">
              <select value={form.status} onChange={(e) => setForm((current) => ({ ...current, status: e.target.value }))} className="input-underline">
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="archived">Archived</option>
              </select>
            </FieldInline>
          </div>
          <FieldInline label="Short description">
            <textarea value={form.short_description} onChange={(e) => setForm((current) => ({ ...current, short_description: e.target.value }))} className="input-underline min-h-[80px]" placeholder="A quick summary shown on the resource card" />
          </FieldInline>
          <FieldInline label="Tags">
            <input value={form.tags} onChange={(e) => setForm((current) => ({ ...current, tags: e.target.value }))} className="input-underline" placeholder="beauty, sourcing, checklist" />
          </FieldInline>
          <FieldInline label="Content">
            <textarea value={form.content} onChange={(e) => setForm((current) => ({ ...current, content: e.target.value }))} className="input-underline min-h-[180px]" placeholder="Detailed content or notes for the resource" />
          </FieldInline>
          <div className="flex gap-3">
            <button type="submit" className="btn-primary">Publish resource</button>
          </div>
        </form>
      </div>

      <div className="editorial-card p-6">
        <h3 className="font-serif text-2xl tracking-tight mb-5">Published resources</h3>
        <div className="space-y-3">
          {resources.map((resource) => (
            <div key={resource.resource_id} className="border border-[--border-soft] p-4 rounded-md">
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="font-serif text-xl">{resource.title}</div>
                <button type="button" onClick={() => removeResource(resource.resource_id)} className="btn-outline text-xs">Delete</button>
              </div>
              <div className="font-mono text-[10px] text-[--muted-foreground] mb-2">{resource.resource_type} · {resource.status} · {resource.visibility}</div>
              <div className="text-xs text-[--muted-foreground]">{resource.short_description || "No summary yet."}</div>
            </div>
          ))}
          {resources.length === 0 && <div className="font-mono text-sm text-[--muted-foreground]">— No resources yet.</div>}
        </div>
      </div>
    </div>
  );
}

function AdminTab({ active, onClick, icon: Icon, label, count }) {
  return <button onClick={onClick} className={`btn-outline text-xs ${active ? "bg-ink text-white border-ink" : ""}`}><Icon className="w-3 h-3" /> {label} <span className="font-mono">{count}</span></button>;
}

function FieldInline({ label, children }) {
  return <label className="block">
    <div className="overline mb-2">{label}</div>
    {children}
  </label>;
}

function Pipeline({ stats }) {
  const stages = ["under_review", "ready_for_bidding", "bidding", "quotes_received", "shortlisting", "sampling", "selected", "procurement", "closed"];
  return <div className="mb-12"><div className="overline mb-4">§ CONTROL TOWER · REQUIREMENT PIPELINE</div><div className="grid grid-cols-2 md:grid-cols-5 lg:grid-cols-9 gap-2">{stages.map((stage) => <div key={stage} className="editorial-card p-4"><div className="font-serif text-3xl">{stats.pipeline?.[stage] || 0}</div><div className="font-mono text-[10px] uppercase tracking-wide mt-2">{stage.replaceAll("_", " ")}</div></div>)}</div></div>;
}

function ManufacturerPanel({ agents, verify, updateVisibility }) {
  const [expandedAgentId, setExpandedAgentId] = useState(null);

  return <>
    <h2 className="font-serif text-3xl tracking-tight mb-6">Manufacturer network</h2>
        <div className="editorial-card overflow-hidden">
          <div className="grid grid-cols-12 gap-4 p-4 border-b border-[--border-soft] overline text-[10px]">
            <div className="col-span-3">Company</div>
            <div className="col-span-2">Account</div>
            <div className="col-span-2">Categories</div>
            <div className="col-span-1">Regions</div>
            <div className="col-span-1">Verified</div>
            <div className="col-span-1">Vis.</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>
          {agents.map((a) => (
            <div key={a.agent_id} className="grid grid-cols-12 gap-4 p-4 border-b border-[--border-soft] items-center" data-testid={`admin-row-${a.agent_id}`}>
              <div className="col-span-3">
                <div className="font-serif text-lg leading-tight">{a.company_name}</div>
                <div className="font-mono text-[10px] text-[--muted-foreground]">{a.agent_id}</div>
              </div>
              <div className="col-span-2">
                <div className="font-mono text-[10px] truncate">{a.email || a.agent_id}</div>
                <div className="text-[10px] text-[--muted-foreground]">Demo Manufacturer · active</div>
              </div>
              <div className="col-span-2 flex flex-wrap gap-1">{(a.categories || []).slice(0, 3).map((c) => <span key={c} className="tag text-[10px]">{c}</span>)}</div>
              <div className="col-span-1 font-mono text-xs truncate">{(a.regions || []).join(", ") || "—"}</div>
              <div className="col-span-1">{a.verified ? <span className="tag verified">YES</span> : <span className="tag">NO</span>}</div>
              <div className="col-span-1">
                <button onClick={() => updateVisibility(a.agent_id, !a.is_public)} className={`tag ${!a.is_public ? "verified" : ""} w-full text-center text-[10px]`}>
                  {a.is_public ? "PUBLIC" : "PRIVATE"}
                </button>
              </div>
              <div className="col-span-2 text-right">
                <div className="flex items-center justify-end gap-2">
                  <button onClick={() => setExpandedAgentId(expandedAgentId === a.agent_id ? null : a.agent_id)} className="btn-outline p-1" aria-expanded={expandedAgentId === a.agent_id}>
                    <ChevronRight className={`w-3 h-3 transition-transform ${expandedAgentId === a.agent_id ? "rotate-90" : ""}`} />
                  </button>
                  {a.verified ? (
                    <button onClick={() => verify(a.agent_id, false)} className="btn-outline text-xs py-1 px-2" data-testid={`unverify-${a.agent_id}`}>
                      <XCircle className="w-3 h-3 inline" />
                    </button>
                  ) : (
                    <button onClick={() => verify(a.agent_id, true)} className="btn-primary text-xs py-1 px-2" data-testid={`verify-${a.agent_id}`}>
                      <CheckCircle2 className="w-3 h-3 inline" />
                    </button>
                  )}
                </div>
              </div>
              {expandedAgentId === a.agent_id && <ManufacturerProfileDetails agent={a} />}
            </div>
          ))}
          {agents.length === 0 && <div className="p-10 text-center text-sm text-[--muted-foreground] font-mono">— No agents yet.</div>}
        </div>
  </>;
}

function ManufacturerProfileDetails({ agent }) {
  const links = [
    ["Factory tour", agent.factory_video_url],
    ["Catalogue", agent.catalogue_url],
  ].filter(([, url]) => url);

  return <div className="col-span-12 mt-2 border-t border-[--border-soft] pt-5 grid md:grid-cols-3 gap-x-8 gap-y-6 text-sm">
    <ProfileDetail label="About" value={agent.bio || agent.tagline || "No description provided."} />
    <ProfileDetail label="Capabilities" value={agent.manufacturing_capabilities?.join(", ") || agent.product_categories?.join(", ") || "Not provided"} />
    <ProfileDetail label="Services" value={agent.services?.join(", ") || "Not provided"} />
    <ProfileDetail label="Factory" value={[agent.business_address, agent.factory_city, agent.factory_state].filter(Boolean).join(", ") || "Not provided"} />
    <ProfileDetail label="Experience" value={agent.years_in_operation ? `${agent.years_in_operation} years in operation` : "Not provided"} />
    <ProfileDetail label="Commercial terms" value={`MOQ: ${agent.min_order_qty || "Not provided"} · ${agent.availability_status || "active"}`} />
    <ProfileDetail label="KYC" value={`PAN: ${agent.pan_number || "Not provided"} · GST: ${agent.gst_number || "Not provided"}`} />
    <ProfileDetail label="Certifications" value={agent.certifications?.join(", ") || "Not provided"} />
    <ProfileDetail label="Turnkey manufacturing" value={agent.turnkey_manufacturing ? "Confirmed" : "Not confirmed"} />
    {links.length > 0 && <div className="md:col-span-3"><div className="overline mb-2">Links</div><div className="flex flex-wrap gap-3">{links.map(([label, url]) => <a key={label} href={url} target="_blank" rel="noreferrer" className="font-mono text-xs underline">{label}</a>)}</div></div>}
    {(agent.portfolio_images || []).length > 0 && <div className="md:col-span-3"><div className="overline mb-2">Portfolio URLs</div><div className="space-y-1">{agent.portfolio_images.map((url) => <a key={url} href={url} target="_blank" rel="noreferrer" className="block font-mono text-xs underline break-all">{url}</a>)}</div></div>}
  </div>;
}

function ProfileDetail({ label, value }) {
  return <div><div className="overline mb-2">{label}</div><div className="text-[--muted-foreground] leading-relaxed">{value}</div></div>;
}

function QuotePanel({ data, onUpdate, samples, onCreateSample, sampleBusy, orders, onCreateOrder }) {
  return <div className="border-t border-[--border-soft] p-5 bg-white/40">
    <div className="overline mb-4">QUOTATIONS · ADMIN REVIEW</div>
    <div className="space-y-3">
      {data.quotes.map((q) => (
        <article key={q.quote_id} className="border-b border-[--border-soft] pb-5 last:border-b-0" data-testid={`admin-quote-${q.quote_id}`}>
          <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-2 mb-4">
            <div>
              <div className="font-serif text-xl leading-tight">{q.agent?.company_name || "Anonymous manufacturer"}</div>
              <div className="font-mono text-[10px] text-[--muted-foreground] mt-1">{q.quote_id} · {q.created_at ? new Date(q.created_at).toLocaleString() : "Submission date unavailable"}</div>
            </div>
            <span className="tag">{(q.status || "pending").replaceAll("_", " ")}</span>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-4">
            <QuoteValue label="Price / unit" value={`₹${q.price_usd ?? "—"}`} />
            <QuoteValue label="Lead time" value={q.lead_time_days != null ? `${q.lead_time_days} days` : "—"} />
            <QuoteValue label="Sample" value={q.sample_available ? (q.sample_pricing_mode === "per_sku" ? "Per SKU" : "Available") : "Not offered"} />
            {q.sample_available && q.sample_pricing_mode !== "per_sku" && <QuoteValue label="Sample cost" value={q.sample_cost_usd != null ? `₹${q.sample_cost_usd}` : "—"} />}
            {q.sample_available && q.sample_pricing_mode !== "per_sku" && <QuoteValue label="Sample lead time" value={q.sample_lead_time_days != null ? `${q.sample_lead_time_days} days` : "—"} />}
            {q.contact_number && <QuoteValue label="Contact number" value={q.contact_number} />}
          </div>

          {q.message && <div className="mb-4">
            <div className="overline text-[10px] mb-1">APPROACH / PITCH</div>
            <p className="text-sm text-[--muted-foreground] whitespace-pre-wrap">{q.message}</p>
          </div>}

          {(q.sku_quotes || []).length > 0 && (
            <div className="mb-4 overflow-x-auto" data-testid={`admin-sku-quotes-${q.quote_id}`}>
              <div className="overline text-[10px] mb-2">PER-SKU PRICING</div>
              <table className="w-full min-w-[520px] text-xs">
                <thead><tr className="border-b border-[--border-soft] text-left font-mono uppercase text-[--muted-foreground]">
                  <th className="p-2">SKU</th><th className="p-2">Price / unit</th><th className="p-2">Lead time</th>
                  {q.sample_available && q.sample_pricing_mode === "per_sku" && <th className="p-2">Sample cost</th>}
                  <th className="p-2">Notes</th>
                </tr></thead>
                <tbody>{q.sku_quotes.map((skuQuote, index) => (
                  <tr key={skuQuote.sku || index} className="border-b border-[--border-soft] last:border-0">
                    <td className="p-2 font-mono">{skuQuote.sku || "—"}</td>
                    <td className="p-2">{skuQuote.price_usd != null ? `₹${skuQuote.price_usd}` : "—"}</td>
                    <td className="p-2">{skuQuote.lead_time_days != null ? `${skuQuote.lead_time_days} days` : "—"}</td>
                    {q.sample_available && q.sample_pricing_mode === "per_sku" && <td className="p-2">{skuQuote.sample_cost_usd != null ? `₹${skuQuote.sample_cost_usd}` : "—"}</td>}
                    <td className="p-2 text-[--muted-foreground]">{skuQuote.notes || "—"}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-[--border-soft]">
            <label className="font-mono text-[10px] uppercase text-[--muted-foreground]" htmlFor={`quote-status-${q.quote_id}`}>Status</label>
            <select id={`quote-status-${q.quote_id}`} value={q.status || "pending"} onChange={(e) => onUpdate(q.quote_id, e.target.value, data.rfq.rfq_id)} className="input-underline w-full sm:w-auto sm:min-w-[170px] text-xs">
              {["pending", "shortlisted", "sampling", "won", "not_selected", "revision_requested"].map((status) => <option key={status} value={status}>{status.replaceAll("_", " ")}</option>)}
            </select>
            {!samples.some((sample) => sample.quote_id === q.quote_id) && ["shortlisted", "won", "sampling"].includes(q.status) && (
              <button onClick={() => onCreateSample(data.rfq.rfq_id, q.quote_id)} disabled={sampleBusy === q.quote_id} className="btn-primary text-xs">{sampleBusy === q.quote_id ? "Creating…" : "Request sample"}</button>
            )}
            {!orders.some((order) => order.quote_id === q.quote_id) && ["won", "confirmed"].includes(q.status) && (
              <button onClick={() => onCreateOrder(data.rfq.rfq_id, q.quote_id)} className="btn-outline text-xs">Create PO</button>
            )}
          </div>
        </article>
      ))}
      {data.quotes.length === 0 && <div className="font-mono text-sm">— No quotes submitted.</div>}
    </div>
  </div>;
}

function QuoteValue({ label, value }) {
  return <div className="min-w-0"><div className="overline text-[10px] mb-1">{label}</div><div className="text-sm break-words">{value}</div></div>;
}

function SampleOverview({ samples, filter, onFilter }) {
  const visibleSamples = filter === "all" ? samples : samples.filter((sample) => sample.status === filter);
  const workflowQuery = filter === "all" ? "" : `?sample_status=${encodeURIComponent(filter)}`;
  return <>
    <div className="flex items-center justify-between gap-4 flex-wrap mb-6">
      <div><h2 className="font-serif text-3xl tracking-tight">Sampling workflow</h2><p className="text-sm text-[--muted-foreground] mt-1">{visibleSamples.length} requests · {filter === "all" ? "all statuses" : filter.replaceAll("_", " ")}</p></div>
      <Link to={`/workflow${workflowQuery}`} className="btn-primary text-xs"><ClipboardList className="w-3 h-3" /> Manage samples</Link>
    </div>
    <label className="block max-w-xs mb-5"><span className="overline text-[10px] block mb-1">FILTER BY STATUS</span><select value={filter} onChange={(event) => onFilter(event.target.value)} className="input-underline w-full text-sm"><option value="all">All statuses</option>{SAMPLE_STATUSES.map((status) => <option key={status} value={status}>{status.replaceAll("_", " ")}</option>)}</select></label>
    <div className="grid gap-3">
      {visibleSamples.map((sample) => <div key={sample.sample_id} className="editorial-card p-5 flex justify-between gap-4 items-center flex-wrap">
        <div><div className="overline mb-2">{sample.sample_id} · {sample.rfq_id}</div><div className="font-serif text-xl">{sample.description || "Production sample"}</div><div className="font-mono text-xs text-[--muted-foreground]">{sample.quote_id} · {sample.agent_id} · {sample.quantity} requested</div></div>
        <div className="flex items-center gap-3"><span className="tag verified">{sample.status.replaceAll("_", " ")}</span><Link to={`/workflow?sample_id=${encodeURIComponent(sample.sample_id)}`} className="btn-outline text-xs">Open request</Link></div>
      </div>)}
      {visibleSamples.length === 0 && <div className="editorial-card p-10 text-center font-mono text-sm">— No requests match this status.</div>}
    </div>
  </>;
}

function ProcurementOverview({ orders }) {
  return <><div className="flex items-center justify-between gap-4 flex-wrap mb-6"><h2 className="font-serif text-3xl tracking-tight">Procurement orders</h2><Link to="/workflow" className="btn-primary text-xs"><ClipboardList className="w-3 h-3" /> Manage orders</Link></div><div className="grid gap-3">{orders.map((order) => <div key={order.po_id} className="editorial-card p-5 flex justify-between gap-4 items-center"><div><div className="overline mb-2">{order.po_id} · {order.rfq_id}</div><div className="font-serif text-xl">{order.quantity} units · ₹{order.total_price_usd}</div><div className="font-mono text-xs text-[--muted-foreground]">{order.quote_id}</div></div><span className="tag verified">{order.status.replaceAll("_", " ")}</span></div>)}{orders.length === 0 && <div className="editorial-card p-10 text-center font-mono text-sm">— No purchase orders yet.</div>}</div></>;
}

function ReportingOverview({ reporting }) {
  if (!reporting) return <div className="editorial-card p-10 text-center font-mono text-sm">— Loading analytics.</div>;
  const groups = [
    ["RFQs by lifecycle", reporting.rfqs?.by_lifecycle_status],
    ["RFQs by bidding state", reporting.rfqs?.by_bidding_status],
    ["Quotes by status", reporting.quotes?.by_status],
    ["Samples by status", reporting.samples?.by_status],
    ["Orders by status", reporting.purchase_orders?.by_status],
  ];
  return <div><h2 className="font-serif text-3xl tracking-tight mb-6">Operational analytics</h2>
    <div className="grid md:grid-cols-4 gap-4 mb-8">
      <Metric label="RFQ award rate" value={`${reporting.win_rates?.rfq_award_rate_percent || 0}%`} />
      <Metric label="Quote win rate" value={`${reporting.win_rates?.quote_win_rate_percent || 0}%`} />
      <Metric label="Avg RFQ → quote" value={formatHours(reporting.turnaround_hours?.rfq_to_first_quote_avg)} />
      <Metric label="Avg PO → complete" value={formatHours(reporting.turnaround_hours?.purchase_order_to_completion_avg)} />
    </div>
    <div className="grid md:grid-cols-2 gap-4">
      {groups.map(([title, values]) => <div key={title} className="editorial-card p-5"><div className="overline mb-3">{title}</div>{Object.entries(values || {}).map(([key, value]) => <div key={key} className="flex justify-between font-mono text-xs py-1"><span>{key.replaceAll("_", " ")}</span><strong>{value}</strong></div>)}</div>)}
    </div>
  </div>;
}

function Metric({ label, value }) {
  return <div className="editorial-card p-5"><div className="font-serif text-3xl">{value}</div><div className="overline mt-2 text-[10px]">{label}</div></div>;
}

function formatHours(value) {
  return value == null ? "—" : `${Math.round(value)}h`;
}

function BiddingPanel({ rfq, onUpdate }) {
  const state = rfq.bidding_status || (rfq.status === "open" ? "active" : "closed");
  return (
    <div className="border-t border-[--border-soft] p-5 bg-white/60">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="overline mb-1">§ BIDDING CONTROL</div>
          <div className="font-serif text-2xl">Manufacturer bidding: {state.replaceAll("_", " ")}</div>
          {rfq.bidding_deadline && <div className="font-mono text-xs text-[--muted-foreground] mt-1">Deadline: {new Date(rfq.bidding_deadline).toLocaleString()}</div>}
        </div>
        <div className="flex gap-2 flex-wrap">
          {rfq.status === "closed" && <button onClick={() => onUpdate("reopen-rfq")} className="btn-primary text-xs">Reopen RFQ</button>}
          {state === "not_started" && <button onClick={() => onUpdate("start")} className="btn-primary text-xs">Start bidding</button>}
          {state === "active" && <button onClick={() => onUpdate("pause")} className="btn-outline text-xs">Pause</button>}
          {state === "paused" && <button onClick={() => onUpdate("reopen")} className="btn-primary text-xs">Reopen</button>}
          {state === "active" && <button onClick={() => onUpdate("extend")} className="btn-outline text-xs">Extend deadline</button>}
          {state !== "closed" && <button onClick={() => onUpdate("close")} className="btn-outline text-xs">Close bidding</button>}
          {state === "closed" && rfq.status !== "closed" && <button onClick={() => onUpdate("reopen")} className="btn-primary text-xs">Reopen</button>}
        </div>
      </div>
    </div>
  );
}

function AuditPanel({ events }) {
  return (
    <div className="border-t border-[--border-soft] p-5 bg-white/40">
      <div className="overline mb-3">§ ACTIVITY HISTORY</div>
      <div className="space-y-2 max-h-56 overflow-auto">
        {events.map((event) => (
          <div key={event.event_id} className="flex items-baseline justify-between gap-4 font-mono text-[10px]">
            <span>{event.action.replaceAll("_", " ")} · {event.role}</span>
            <span className="text-[--muted-foreground]">{new Date(event.timestamp).toLocaleString()}</span>
          </div>
        ))}
        {events.length === 0 && <div className="font-mono text-xs text-[--muted-foreground]">— No activity recorded yet.</div>}
      </div>
    </div>
  );
}

function ParticipantPanel({ agents, participants, onUpdate }) {
  const invited = new Set(participants.filter((p) => ["invited", "accepted"].includes(p.status)).map((p) => p.agent_id));
  return (
    <div className="border-t border-[--border-soft] p-5 bg-klein/5">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div>
          <div className="overline mb-1">§ CLOSED-RFP PARTICIPANTS</div>
          <div className="font-serif text-2xl">Assigned manufacturers</div>
        </div>
        <div className="font-mono text-xs text-[--muted-foreground]">{participants.filter((p) => p.status !== "removed").length} assigned</div>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        {agents.map((agent) => {
          const participant = participants.find((p) => p.agent_id === agent.agent_id);
          const isAssigned = invited.has(agent.agent_id);
          return (
            <div key={agent.agent_id} className="border border-[--border-soft] bg-white/60 p-4 flex items-center justify-between gap-3">
              <div>
                <div className="font-serif text-lg">{agent.company_name}</div>
                <div className="font-mono text-[10px] text-[--muted-foreground]">
                  {participant ? `${participant.status} · ${participant.quote_count || 0} quote${participant.quote_count === 1 ? "" : "s"}` : "Not assigned"}
                </div>
              </div>
              {isAssigned ? (
                <button onClick={() => onUpdate(agent.agent_id, "remove")} className="btn-outline text-xs">Remove</button>
              ) : (
                <button onClick={() => onUpdate(agent.agent_id, "invite")} className="btn-primary text-xs">Invite</button>
              )}
            </div>
          );
        })}
      </div>
      {agents.length === 0 && <div className="font-mono text-xs">— No manufacturers available.</div>}
    </div>
  );
}

function QuoteOverview({ requirements, onOpen }) {
  return <><h2 className="font-serif text-3xl tracking-tight mb-6">Quote activity</h2><div className="grid gap-3">{requirements.filter((r) => r.quote_count).map((r) => <button key={r.rfq_id} onClick={() => onOpen(r.rfq_id)} className="editorial-card p-5 text-left flex justify-between"><span><span className="overline block mb-2">{r.rfq_id}</span><span className="font-serif text-2xl">{r.title}</span></span><span className="font-mono text-xs">{r.quote_count} quotes →</span></button>)}</div></>;
}

function KnowledgeAndSectors({ sectors, knowledge, onReload }) {
  const [view, setView] = useState("sectors");
  return (
    <div className="space-y-6">
      <div className="flex gap-2 mb-6">
        <button onClick={() => setView("sectors")} className={`btn-outline text-xs ${view === "sectors" ? "bg-klein text-white" : ""}`}>Manage Sectors</button>
        <button onClick={() => setView("knowledge")} className={`btn-outline text-xs ${view === "knowledge" ? "bg-klein text-white" : ""}`}>Manage Knowledge</button>
      </div >
      {view === "sectors" ? <SectorManager sectors={sectors} onReload={onReload} /> : <KnowledgeManager sectors={sectors} knowledge={knowledge} onReload={onReload} />}
    </div >
  );
}


function SectorManager({ sectors, onReload }) {
  const [editingSector, setEditingSector] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [subCategoriesText, setSubCategoriesText] = useState("");
  const [sectorForm, setSectorForm] = useState({ name: "", description: "", parent_sector_id: null, sort_order: 0, active: true, icon: "", opportunity_teaser: "", sub_categories: [] });

  const saveSector = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...sectorForm,
        sub_categories: subCategoriesText.split(",").map((item) => item.trim()).filter(Boolean),
      };
      if (editingSector) {
        await axios.put(`${API}/admin/sectors/${editingSector}`, payload);
      } else {
        await axios.post(`${API}/admin/sectors`, payload);
      }
      toast.success("Sector saved");
      onReload();
      setShowForm(false);
      setEditingSector(null);
      setSectorForm({ name: "", description: "", parent_sector_id: null, sort_order: 0, active: true, icon: "", opportunity_teaser: "", sub_categories: [] });
      setSubCategoriesText("");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save sector"); }
  };

  const deleteSector = async (id) => {
    if (!confirm("Delete this sector? All linked templates may be affected.")) return;
    try {
      await axios.delete(`${API}/admin/sectors/${id}`);
      toast.success("Sector deleted");
      onReload();
    } catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="editorial-card p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-serif text-3xl tracking-tight">Sector Management</h2>
        <button onClick={() => { setEditingSector(null); setShowForm(true); }} className="btn-primary text-xs">+ Add Sector</button>
      </div >
      {showForm && (
        <form onSubmit={saveSector} className="bg-bone p-4 mb-6 border-l-4 border-l-klein space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <input value={sectorForm.name} onChange={e => setSectorForm({...sectorForm, name: e.target.value})} placeholder="Sector Name" className="input-underline text-sm" required />
            <select value={sectorForm.parent_sector_id || ""} onChange={e => setSectorForm({...sectorForm, parent_sector_id: e.target.value || null})} className="input-underline text-sm">
              <option value="">No Parent (Top Level)</option>
              {sectors.filter(s => s.sector_id !== editingSector).map(s => <option key={s.sector_id} value={s.sector_id}>{s.name}</option>)}
            </select>
          </div >
          <div className="grid grid-cols-3 gap-3">
            <input value={sectorForm.icon || ""} onChange={e => setSectorForm({...sectorForm, icon: e.target.value})} placeholder="Icon (emoji/lucide)" className="input-underline text-sm" />
            <input type="number" value={sectorForm.sort_order} onChange={e => setSectorForm({...sectorForm, sort_order: parseInt(e.target.value) || 0})} placeholder="Sort Order" className="input-underline text-sm" />
            <div className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={sectorForm.active} onChange={e => setSectorForm({...sectorForm, active: e.target.checked})} id="sector-active" />
              <label htmlFor="sector-active">Active/Visible</label>
            </div >
          </div >
          <div className="grid grid-cols-2 gap-3">
            <input value={sectorForm.opportunity_teaser || ""} onChange={e => setSectorForm({...sectorForm, opportunity_teaser: e.target.value})} placeholder="Opportunity Teaser (e.g. ₹ 12 L Cr market)" className="input-underline text-sm" />
            <input value={subCategoriesText} onChange={(e) => setSubCategoriesText(e.target.value)} placeholder="Sub-categories (comma separated)" className="input-underline text-sm" />
          </div >
          <textarea value={sectorForm.description} onChange={e => setSectorForm({...sectorForm, description: e.target.value})} placeholder="Description" className="input-underline text-sm w-full h-20" />
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setShowForm(false)} className="btn-outline text-xs">Cancel</button>
            <button type="submit" className="btn-primary text-xs">Save Sector</button>
          </div >
        </form>
      )}
      <div className="space-y-2">
        {sectors.map(s => (
          <div key={s.sector_id} className="flex items-center justify-between p-3 border border-[--border-soft] hover:border-klein transition-colors group">
            <div >
              <div className="font-serif text-lg">{s.icon && <span className="mr-2">{s.icon}</span>}{s.name}</div>
              <div className="font-mono text-[10px] text-[--muted-foreground] space-y-1">
                <div className="block">ID: {s.sector_id} {s.parent_sector_id && `• Parent: ${sectors.find(p => p.sector_id === s.parent_sector_id)?.name}`}</div>
                {s.opportunity_teaser && <div className="block text-klein font-bold">{s.opportunity_teaser}</div>}
                {s.sub_categories?.length > 0 && <div className="block italic">Subs: {s.sub_categories.join(", ")}</div>}
              </div >
            </div >
            <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
              <button onClick={() => { setEditingSector(s.sector_id); setSectorForm(s); setSubCategoriesText((s.sub_categories || []).join(", ")); setShowForm(true); }} className="btn-outline text-[10px]">Edit</button>
              <button onClick={() => deleteSector(s.sector_id)} className="btn-outline text-[10px] text-red-600">Delete</button>
            </div >
          </div >
        ))}
      </div >
    </div >
  );
}


function KnowledgeManager({ sectors, knowledge, onReload }) {
  const [editingKb, setEditingKb] = useState(null);
  const [showKbForm, setShowKbForm] = useState(false);
  const [kbForm, setKbForm] = useState({ sector_id: "", title: "", content: "{}" });

  const saveKb = async (e) => {
    e.preventDefault();
    try {
      let finalContent;
      try {
        finalContent = typeof kbForm.content === 'string' ? JSON.parse(kbForm.content) : kbForm.content;
      } catch (err) {
        throw new Error("Invalid JSON in Blueprint Data");
      }

      const payload = { ...kbForm, content: finalContent };

      if (editingKb) {
        await axios.put(`${API}/admin/knowledge/${editingKb}`, payload);
      } else {
        await axios.post(`${API}/admin/knowledge`, payload);
      }
      toast.success("Knowledge updated");
      onReload();
      setShowKbForm(false);
      setEditingKb(null);
      setKbForm({ sector_id: "", title: "", content: "{}" });
    } catch (err) { toast.error(err.message || err.response?.data?.detail || "Failed to save knowledge"); }
  };

  const deleteKb = async (id) => {
    if (!confirm("Delete this knowledge entry?")) return;
    try {
      await axios.delete(`${API}/admin/knowledge/${id}`);
      toast.success("Entry deleted");
      onReload();
    } catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="editorial-card p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-serif text-3xl tracking-tight">Knowledge Repository</h2>
        <button onClick={() => { setEditingKb(null); setShowKbForm(true); }} className="btn-primary text-xs">+ Add Knowledge</button>
      </div >
      {showKbForm && (
        <form onSubmit={saveKb} className="bg-bone p-4 mb-6 border-l-4 border-l-klein space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <select value={kbForm.sector_id} onChange={e => setKbForm({...kbForm, sector_id: e.target.value})} className="input-underline text-sm" required>
              <option value="">Select Sector</option>
              {sectors.map(s => <option key={s.sector_id} value={s.sector_id}>{s.name}</option>)}
            </select>
            <input value={kbForm.title} onChange={e => setKbForm({...kbForm, title: e.target.value})} placeholder="Entry Title" className="input-underline text-sm" required />
          </div >
          <div className="overline text-[10px] mb-1">Blueprint Data (JSON)</div>
          <textarea 
            value={kbForm.content} 
            onChange={e => setKbForm({...kbForm, content: e.target.value})} 
            placeholder='{ "market_size_india_inr_cr": 1000, ... }' 
            className="input-underline text-sm font-mono w-full h-32" 
            required 
          />
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setShowKbForm(false)} className="btn-outline text-xs">Cancel</button>
            <button type="submit" className="btn-primary text-xs">Save Knowledge</button>
          </div >
        </form>
      )}
      <div className="space-y-2">
        {knowledge.map(k => (
          <div key={k.kb_id} className="flex items-center justify-between p-3 border border-[--border-soft] hover:border-klein transition-colors group">
            <div >
              <div className="font-serif text-lg">{k.title}</div>
              <div className="font-mono text-[10px] text-[--muted-foreground]">Sector: {sectors.find(s => s.sector_id === k.sector_id)?.name || "Unknown"}</div>
            </div >
            <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
              <button onClick={() => { setEditingKb(k.kb_id); setKbForm({ ...k, content: JSON.stringify(k.content, null, 2) }); setShowKbForm(true); }} className="btn-outline text-[10px]">Edit</button>
              <button onClick={() => deleteKb(k.kb_id)} className="btn-outline text-[10px] text-red-600">Delete</button>
            </div >
          </div >
        ))}
      </div >
    </div >
  );
}

